import React, { Dispatch, FC, SetStateAction, useMemo, useState } from 'react'
import { Box, Typography } from '@mui/material'
import TrendsFilterIconDefault from '../../../../components/svg/coverage-analyzer/TrendsFilterIconDefault'

/* Utils */
import {
  ALL_SOURCES_SENTINEL,
  AnalyzerData,
  CPITimePeriod,
  IntegrationInstance,
  LastSeenWithin,
  OperatingSystem
} from '../../../../../models'

/* Components */
import { ChartHeader, ChartWrapper } from './trendsTab.styles'
import AnalysisChart from './components/AnalysisChart'
import FilterComponent from '../../components/FilterComponent'
import { DEFAULT_TRENDS_TIME_PERIOD } from '../../utils'
import AnalysisChartDescription from './components/AnalysisChartDescription'
import ThemeButton from '../../../../components/buttons/primary/ThemeButton'

interface Props {
  firstSource: string
  secondSource: string
  selectedOperatingSystem: OperatingSystem
  setSelectedOperatingSystem: Dispatch<SetStateAction<OperatingSystem>>
  chartData: AnalyzerData | null
  selectedTimePeriod: CPITimePeriod
  setSelectedTimePeriod: Dispatch<SetStateAction<CPITimePeriod>>
  fetchTrendData: ({
    timePeriod,
    seenWithin,
    operatingSystem
  }: {
    timePeriod?: CPITimePeriod
    seenWithin?: LastSeenWithin
    operatingSystem?: OperatingSystem
  }) => void
  instances: IntegrationInstance[]
  selectedInstances: IntegrationInstance[]
  isTrendsFilterActive: boolean
  setIsTrendsFilterActive: Dispatch<SetStateAction<boolean>>
  chartLoading: boolean
  isCompareToAll?: boolean
  sentinelInSourceA?: boolean
}

const CoverageTrendsTab: FC<Props> = ({
  firstSource,
  secondSource,
  selectedOperatingSystem,
  setSelectedOperatingSystem,
  chartData,
  selectedTimePeriod,
  setSelectedTimePeriod,
  fetchTrendData,
  instances,
  selectedInstances,
  isTrendsFilterActive,
  setIsTrendsFilterActive,
  chartLoading,
  isCompareToAll,
  sentinelInSourceA = false
}) => {
  const [showFilter, setShowFilter] = useState(false)

  /* The trend response is keyed differently per endpoint (see fetchTrendData in
   * useCoverageAnalyzerData): the v4 endpoints — Compare-to-All and the two-source v2 trend — key
   * series by instance_name slug (+ the "__all__" sentinel), while the legacy v3 endpoint keys by
   * instance id. The v2 endpoint is used only when BOTH selected instances are v2, mirroring the
   * fetch's isV2 gate. The chart lookup (AnalysisChart) must use whichever key the active request
   * produced, otherwise parts.find never matches and the chart renders empty. */
  const isV2Trend = Boolean(selectedInstances[0]?.is_v2 && selectedInstances[1]?.is_v2)

  /* In Compare-to-All, assign the sentinel vs picked series to first/second based on which slot
   * holds "All" (see docs/api-spec2.md). */
  const firstSourceId = useMemo(() => {
    if (isCompareToAll) return sentinelInSourceA ? ALL_SOURCES_SENTINEL : selectedInstances[0]?.instance_name || ''
    return isV2Trend ? selectedInstances[0]?.instance_name || '' : selectedInstances[0]?.id || ''
  }, [selectedInstances, isCompareToAll, sentinelInSourceA, isV2Trend])

  const secondSourceId = useMemo(() => {
    if (isCompareToAll) return sentinelInSourceA ? selectedInstances[0]?.instance_name || '' : ALL_SOURCES_SENTINEL
    return isV2Trend ? selectedInstances[1]?.instance_name || '' : selectedInstances[1]?.id || ''
  }, [selectedInstances, isCompareToAll, sentinelInSourceA, isV2Trend])

  const resetFilter = () => {
    setSelectedTimePeriod(DEFAULT_TRENDS_TIME_PERIOD)
    setSelectedOperatingSystem(OperatingSystem.All)
    setIsTrendsFilterActive(false)
    fetchTrendData({ timePeriod: DEFAULT_TRENDS_TIME_PERIOD, operatingSystem: OperatingSystem.All })
  }

  let firstSourceName: string | undefined = selectedInstances[0]?.name
  let secondSourceName: string | undefined = selectedInstances[1]?.name
  if (isCompareToAll) {
    /* The picked product is always selectedInstances[0]; the other leg is the "All" union. Render
     * "All" under the slot where it was actually selected (Source A or Source B). */
    const pickedName = selectedInstances[0]?.name
    firstSourceName = sentinelInSourceA ? 'All' : pickedName
    secondSourceName = sentinelInSourceA ? pickedName : 'All'
  }

  return (
    <>
      <ChartWrapper>
        <Box className="chart-container">
          <ChartHeader>
            <Box className="info-block">
              <Box>
                <Typography className="square blue" />
                <Typography className="name">
                  {isCompareToAll && sentinelInSourceA ? 'All' : selectedInstances[0]?.name}
                </Typography>
              </Box>

              <Box>
                <Typography className="square orange" />
                <Typography className="name">
                  {isCompareToAll
                    ? sentinelInSourceA
                      ? selectedInstances[0]?.name
                      : 'All'
                    : selectedInstances[1]?.name}
                </Typography>
              </Box>
            </Box>

            <ThemeButton
              themevariant={showFilter ? 'ghost' : 'secondary'}
              className="chart-header-filters-button"
              onClick={() => setShowFilter(!showFilter)}
              noActive
            >
              <>
                {!isTrendsFilterActive ? (
                  <TrendsFilterIconDefault />
                ) : (
                  <Box className="filter-icon-block">
                    <TrendsFilterIconDefault />
                    <Box className="active-circle">
                      <Box className="circle" />
                    </Box>
                  </Box>
                )}
                Filters
              </>
            </ThemeButton>
          </ChartHeader>

          <Box className="chart-wrapper">
            <AnalysisChart
              selectedTimePeriod={selectedTimePeriod}
              chartData={chartData}
              firstSourceId={firstSourceId}
              secondSourceId={secondSourceId}
              instances={instances}
              chartLoading={chartLoading}
              firstSourceName={firstSourceName}
              secondSourceName={secondSourceName}
            />
          </Box>
        </Box>

        <FilterComponent showFilter={showFilter} setShowFilter={setShowFilter} resetFilter={resetFilter}>
          <AnalysisChartDescription
            setIsFilterActive={setIsTrendsFilterActive}
            firstSource={firstSource}
            secondSource={secondSource}
            selectedOperatingSystem={selectedOperatingSystem}
            setSelectedOperatingSystem={setSelectedOperatingSystem}
            selectedTimePeriod={selectedTimePeriod}
            setSelectedTimePeriod={setSelectedTimePeriod}
            fetchTrendData={fetchTrendData}
          />
        </FilterComponent>
      </ChartWrapper>
    </>
  )
}

export default CoverageTrendsTab
